import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Trophy, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export function TopVoiceWinnerBanner() {
  const { user } = useAuth();
  const [show, setShow] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!user) return;

    const checkWinner = async () => {
      // Check if already dismissed this month
      const dismissKey = `top_voice_dismissed_${new Date().toISOString().slice(0, 7)}`;
      if (localStorage.getItem(dismissKey)) return;

      // Check if user has top_voice_winner badge
      const { data } = await supabase
        .from('leaderboard_scores' as any)
        .select('badge_key')
        .eq('user_id', user.id)
        .maybeSingle();

      if ((data as any)?.badge_key === 'top_voice_winner') {
        // Verify they actually have 3 consecutive months
        const months: string[] = [];
        const now = new Date();
        for (let i = 1; i <= 3; i++) {
          const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
          months.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
        }

        const { data: history } = await supabase
          .from('leaderboard_monthly_history' as any)
          .select('final_rank')
          .eq('user_id', user.id)
          .in('month_year', months);

        const top3Count = ((history as any[]) || []).filter((h: any) => h.final_rank <= 3).length;
        if (top3Count >= 3) {
          setShow(true);
        }
      }
    };

    checkWinner();
  }, [user?.id]);

  const handleDismiss = () => {
    setDismissed(true);
    const dismissKey = `top_voice_dismissed_${new Date().toISOString().slice(0, 7)}`;
    localStorage.setItem(dismissKey, 'true');
    setTimeout(() => setShow(false), 400);
  };

  if (!show) return null;

  return (
    <AnimatePresence>
      {!dismissed && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: -20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: -20 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="mb-8 relative overflow-hidden rounded-2xl border-2 border-amber-400/40"
        >
          {/* Animated background */}
          <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 via-primary/5 to-emerald-500/10" />
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-amber-400/10 to-transparent pointer-events-none"
            animate={{ x: ['-100%', '100%'] }}
            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
          />

          {/* Pulsing border */}
          <motion.div
            className="absolute inset-0 rounded-2xl border-2 border-amber-400/30 pointer-events-none"
            animate={{ opacity: [0.3, 0.8, 0.3] }}
            transition={{ duration: 2, repeat: Infinity }}
          />

          <div className="relative p-6 md:p-8">
            <button
              onClick={handleDismiss}
              className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="flex flex-col items-center text-center gap-4">
              <motion.div
                className="p-4 rounded-2xl bg-amber-500/15 ring-2 ring-amber-400/30"
                animate={{ scale: [1, 1.05, 1], rotate: [0, 2, -2, 0] }}
                transition={{ duration: 3, repeat: Infinity }}
              >
                <Trophy className="h-10 w-10 text-amber-500" />
              </motion.div>

              <div>
                <h2 className="text-2xl md:text-3xl font-extrabold text-foreground flex items-center justify-center gap-2">
                  <Sparkles className="h-6 w-6 text-amber-500" />
                  You're a Top Voice!
                  <Sparkles className="h-6 w-6 text-amber-500" />
                </h2>
                <p className="text-foreground/80 mt-2 text-base">
                  You finished in the top 3 for 3 consecutive months.
                  <br />
                  Your reward is on the way.
                </p>
                <p className="text-muted-foreground text-sm mt-3">
                  Our support team will deliver your reward within 72 hours.
                </p>
              </div>

              <Button
                onClick={handleDismiss}
                variant="outline"
                className="rounded-xl mt-2 border-amber-400/30 hover:bg-amber-500/10"
              >
                Got it — thank you! 🎉
              </Button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
